import {ActivityCarBusState} from "../../types-enums/activity-car-bus";
import {roundKm} from "../math/round-values";

/**
 * Determines the distance in kilometers based on the provided manual entry or a calculated value.
 *
 * If a manually entered distance is provided (not null), it will take precedence and be returned.
 * If no manual entry is provided, the calculated distance will be returned. If both are null,
 * the result will be null.
 *
 * @param {number | null} manualEnteredDistanceKm - The distance manually entered by the user in kilometers.
 * This value will be prioritized if provided.
 * @param {number | null} calculatedDistanceKm - The distance calculated by the system in kilometers.
 * This value is used if no manual entry is provided.
 * @returns {number | null} The prioritized distance in kilometers, or null if both inputs are null.
 */
export const getCalculatedOrManualEnteredDistanceKm = (manualEnteredDistanceKm: number | null, calculatedDistanceKm: number | null): number | null => {
    let distanceKm: number | null = null
    if (manualEnteredDistanceKm !== null) {
        distanceKm = manualEnteredDistanceKm
    } else {
        distanceKm = calculatedDistanceKm
    }

    return distanceKm
}

/**
 * Calculates the total distance in kilometers for a group of car and bus activities that share the same group ID.
 *
 * @param {ActivityCarBusState} carBus - The current car or bus activity whose group ID is used for filtering.
 * @param {ActivityCarBusState[]} allCarBus - An array of all car or bus activity states.
 * @returns {number} The total distance in kilometers for the car or bus group, rounded to the nearest kilometer.
 */
export const getDistanceKmOfCarBusGroup = (carBus: ActivityCarBusState, allCarBus: ActivityCarBusState[]) => {
    const {common: {groupId}} = carBus
    const carBusWithGroupId = allCarBus.filter(carBus => carBus.common.groupId === groupId)
    const distanceKm = carBusWithGroupId.reduce((acc, carBus) => {
        const {manualEnteredDistanceKm, calculatedDistanceKm} = carBus
        const distanceKmOfCarBus = getCalculatedOrManualEnteredDistanceKm(manualEnteredDistanceKm, calculatedDistanceKm)
        if (distanceKmOfCarBus !== null) return acc + distanceKmOfCarBus
        return acc
    }, 0)
    return roundKm(distanceKm)
}